/**
 * Modal / Dialog (design §7.7).
 * Desktop: centered Radix dialog. Mobile: Vaul bottom sheet.
 */
import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Drawer } from 'vaul';
import { m } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-layers';
import { FOCUS_RING } from './focus';
import { useIsMobile } from '@/hooks/use-mobile';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}

function ModalBody({
  title,
  children,
  actions,
  onClose,
  className,
}: Omit<ModalProps, 'open'>) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-[16px] leading-6 font-semibold text-tinta">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.modal.close')}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg bg-superficie-2 text-tinta-2 transition-all duration-150 hover:bg-linea-fuerte hover:text-tinta active:scale-95',
            FOCUS_RING,
          )}
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
      <div className={cn('mt-3 text-[14px] leading-[21px] text-tinta', className)}>{children}</div>
      {actions && <div className="mt-6 flex items-center justify-end gap-3">{actions}</div>}
    </>
  );
}

export function Modal({ open, onClose, title, children, actions, className }: ModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <Drawer.Portal>
          <Drawer.Overlay
            className="fixed inset-0 bg-[rgba(32,40,42,0.32)] backdrop-blur-[4px]"
            style={{ zIndex: Z.modal }}
          />
          <Drawer.Content
            style={{ zIndex: Z.modal }}
            className={cn(
              'fixed inset-x-0 bottom-0 flex max-h-[min(90dvh,640px)] flex-col rounded-t-[14px] border-t-2 border-linea-fuerte bg-superficie px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 shadow-popover outline-none',
              className,
            )}
          >
            <Drawer.Handle className="mx-auto mb-4 h-1 w-10 shrink-0 rounded-full bg-linea-fuerte" aria-hidden />
            <div className="overflow-y-auto">
              <ModalBody onClose={onClose} title={title} actions={actions} className={className}>
                {children}
              </ModalBody>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay asChild>
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{ zIndex: Z.modal }}
            className="fixed inset-0 bg-[rgba(32,40,42,0.32)] backdrop-blur-[4px]"
          />
        </DialogPrimitive.Overlay>
        <DialogPrimitive.Content asChild>
          <m.div
            initial={{ opacity: 0, scale: 0.97, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            style={{ zIndex: Z.modal, translate: '-50% -50%' }}
            className={cn(
              'fixed left-1/2 top-1/2 w-full max-w-[480px]',
              'rounded-card border-2 border-linea-fuerte bg-superficie p-6 shadow-popover',
              'max-h-[calc(100dvh-2rem)] overflow-y-auto',
              className,
            )}
          >
            <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
            <ModalBody onClose={onClose} title={title} actions={actions} className={className}>
              {children}
            </ModalBody>
          </m.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
